import type { EmailMessage, EmailMessageSummary } from "schemas/taskEmails";
import type {
  CreateTaskRequest,
  Task,
  TaskRunWithTask,
  UpdateTaskInput,
} from "schemas/tasks";

type RequestOptions = {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
};

export function createTasksClient({
  request,
}: {
  request: <ResponseType>(options: RequestOptions) => Promise<ResponseType>;
}) {
  return {
    find: async (input: { projectId: string }): Promise<Task[]> => {
      const result = await request<{ tasks: Task[] }>({
        method: "GET",
        path: "/api/v1/tasks",
        query: { projectId: input.projectId },
      });
      return result.tasks;
    },
    findRuns: async (input: {
      projectId: string;
      limit?: number;
    }): Promise<TaskRunWithTask[]> => {
      const result = await request<{ runs: TaskRunWithTask[] }>({
        method: "GET",
        path: "/api/v1/tasks/runs",
        query: { projectId: input.projectId, limit: input.limit },
      });
      return result.runs;
    },
    create: async (input: CreateTaskRequest): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "POST",
        path: "/api/v1/tasks",
        body: input,
      });
      return result.task;
    },
    update: async ({
      input: taskInput,
      taskId,
    }: {
      taskId: string;
      input: UpdateTaskInput;
    }): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "PUT",
        path: `/api/v1/tasks/${encodeURIComponent(taskId)}`,
        body: taskInput,
      });
      return result.task;
    },
    runNow: async (input: {
      taskId: string;
    }): Promise<{ queued: boolean; run: TaskRunWithTask; task: Task }> => {
      const result = await request<{
        queued: boolean;
        run: TaskRunWithTask;
        task: Task;
      }>({
        method: "POST",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}/run`,
      });
      return result;
    },
    delete: async (input: {
      taskId: string;
    }): Promise<{ deleted: true; task: Task }> => {
      return await request<{ deleted: true; task: Task }>({
        method: "DELETE",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}`,
      });
    },
    pause: async (input: { taskId: string }): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "POST",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}/pause`,
      });
      return result.task;
    },
    resume: async (input: { taskId: string }): Promise<Task> => {
      const result = await request<{ task: Task }>({
        method: "POST",
        path: `/api/v1/tasks/${encodeURIComponent(input.taskId)}/resume`,
      });
      return result.task;
    },
    findEmails: async (input: {
      projectId: string;
      taskId?: string;
      limit?: number;
    }): Promise<EmailMessageSummary[]> => {
      const result = await request<{ emails: EmailMessageSummary[] }>({
        method: "GET",
        path: "/api/v1/tasks/emails",
        query: {
          projectId: input.projectId,
          taskId: input.taskId,
          limit: input.limit,
        },
      });
      return result.emails;
    },
    getEmail: async (input: {
      projectId: string;
      emailId: string;
    }): Promise<EmailMessage> => {
      const result = await request<{ email: EmailMessage }>({
        method: "GET",
        path: `/api/v1/tasks/emails/${encodeURIComponent(input.emailId)}`,
        query: { projectId: input.projectId },
      });
      return result.email;
    },
    previewRecipeEmail: async (input: {
      projectId: string;
      recipeId: string;
    }): Promise<{ subject: string; html: string }> => {
      return await request<{ subject: string; html: string }>({
        method: "GET",
        path: `/api/v1/tasks/recipes/${encodeURIComponent(input.recipeId)}/email-preview`,
        query: { projectId: input.projectId },
      });
    },
    sendRecipeTestEmail: async (input: {
      projectId: string;
      recipeId: string;
    }): Promise<EmailMessageSummary[]> => {
      const result = await request<{ emails: EmailMessageSummary[] }>({
        method: "POST",
        path: `/api/v1/tasks/recipes/${encodeURIComponent(input.recipeId)}/test-email`,
        body: { projectId: input.projectId },
      });
      return result.emails;
    },
  };
}

export type TasksClient = ReturnType<typeof createTasksClient>;
